import React, { useState, useRef } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCode, FiCopy, FiCheck, FiDownload, FiSliders } = FiIcons;

const CodeGenerator = ({ checklist, darkMode }) => {
  const [copied, setCopied] = useState(false);
  const [codeType, setCodeType] = useState('widget');
  const [customization, setCustomization] = useState({
    buttonText: "✓ Marcos",
    buttonPosition: "bottom-right",
    buttonGradientStart: "#667eea",
    buttonGradientEnd: "#764ba2",
    activeButtonGradientStart: "#f87171",
    activeButtonGradientEnd: "#ef4444",
    buttonSize: "medium",
    buttonBorderRadius: "full",
    buttonShadow: "medium",
    adaptiveStyle: true,
    showProgressInButton: true,
    showProgressBar: true,
    progressBarColor: "#10b981",
    // Checklist customization
    showTitle: true,
    showDescription: true,
    itemBackgroundColor: "#ffffff",
    itemBorderColor: "#e5e7eb",
    itemHoverColor: "#f3f4f6",
    completedItemColor: "#f0fdf4",
    completedBorderColor: "#10b981",
    checkboxColor: "#10b981",
    textColor: "#374151",
    linkColor: "#3b82f6",
    modalBackgroundColor: "#ffffff",
    modalBorderRadius: "16px",
    itemBorderRadius: "12px"
  });
  const textareaRef = useRef();

  const handleCustomizationChange = (field, value) => {
    setCustomization(prev => ({ ...prev, [field]: value }));
  };

  const getPositionStyles = (position) => {
    switch(position) {
      case 'bottom-right':
        return 'bottom: 30px !important; right: 30px !important;';
      case 'bottom-left':
        return 'bottom: 30px !important; left: 30px !important;';
      case 'top-right':
        return 'top: 30px !important; right: 30px !important;';
      case 'top-left':
        return 'top: 30px !important; left: 30px !important;';
      case 'middle-right':
        return 'top: 50% !important; right: 30px !important; transform: translateY(-50%) !important;';
      case 'middle-left':
        return 'top: 50% !important; left: 30px !important; transform: translateY(-50%) !important;';
      default:
        return 'bottom: 30px !important; right: 30px !important;';
    }
  };

  const getBorderRadiusStyle = () => {
    switch(customization.buttonBorderRadius) {
      case 'none': return '0px';
      case 'small': return '6px';
      case 'medium': return '12px';
      case 'large': return '24px';
      case 'full': return '50px';
      default: return '50px';
    }
  };

  const getShadowStyle = () => {
    switch(customization.buttonShadow) {
      case 'none': return 'none';
      case 'small': return '0 2px 10px rgba(0,0,0,0.15)';
      case 'medium': return '0 8px 25px rgba(0,0,0,0.25)';
      case 'large': return '0 12px 35px rgba(0,0,0,0.35)';
      default: return '0 8px 25px rgba(0,0,0,0.25)';
    }
  };

  const getButtonSize = () => {
    switch(customization.buttonSize) {
      case 'small': return 'min-width: 45px; height: 45px; font-size: 14px;';
      case 'medium': return 'min-width: 60px; height: 60px; font-size: 16px;';
      case 'large': return 'min-width: 75px; height: 75px; font-size: 18px;';
      default: return 'min-width: 60px; height: 60px; font-size: 16px;';
    }
  };

  const generateWidgetCode = () => {
    const checklistData = JSON.stringify(checklist, null, 2);
    const positionStyles = getPositionStyles(customization.buttonPosition);
    const borderRadius = getBorderRadiusStyle();
    const shadowStyle = getShadowStyle();
    const buttonSize = getButtonSize();
    
    return `<!-- N1P Checklist Widget Flutuante Universal -->
<!-- Este código funciona em qualquer lugar: WordPress, Elementor, HTML, etc. -->
<script>
(function() {
  // Evita múltiplas execuções
  if (window.n1pChecklistWidget_${checklist.id}) return;
  window.n1pChecklistWidget_${checklist.id} = true;
  
  // Configuração personalizada
  const customization = {
    buttonText: "${customization.buttonText}",
    buttonPosition: "${customization.buttonPosition}",
    buttonGradientStart: "${customization.buttonGradientStart}",
    buttonGradientEnd: "${customization.buttonGradientEnd}",
    activeButtonGradientStart: "${customization.activeButtonGradientStart}",
    activeButtonGradientEnd: "${customization.activeButtonGradientEnd}",
    buttonSize: "${customization.buttonSize}",
    buttonBorderRadius: "${customization.buttonBorderRadius}",
    buttonShadow: "${customization.buttonShadow}",
    adaptiveStyle: ${customization.adaptiveStyle},
    showProgressInButton: ${customization.showProgressInButton},
    showProgressBar: ${customization.showProgressBar},
    progressBarColor: "${customization.progressBarColor}",
    showTitle: ${customization.showTitle},
    showDescription: ${customization.showDescription},
    itemBackgroundColor: "${customization.itemBackgroundColor}",
    itemBorderColor: "${customization.itemBorderColor}",
    itemHoverColor: "${customization.itemHoverColor}",
    completedItemColor: "${customization.completedItemColor}",
    completedBorderColor: "${customization.completedBorderColor}",
    checkboxColor: "${customization.checkboxColor}",
    textColor: "${customization.textColor}",
    linkColor: "${customization.linkColor}",
    modalBackgroundColor: "${customization.modalBackgroundColor}",
    modalBorderRadius: "${customization.modalBorderRadius}",
    itemBorderRadius: "${customization.itemBorderRadius}"
  };
  
  // Dados do checklist
  const checklistData = ${checklistData};
  
  // Detectar cores do site automaticamente (estilo adaptativo)
  function getAdaptiveColors() {
    if (!customization.adaptiveStyle) return {};
    
    try {
      const bodyStyles = window.getComputedStyle(document.body);
      const primaryColor = bodyStyles.getPropertyValue('--primary-color') || 
                          bodyStyles.getPropertyValue('--theme-color') ||
                          bodyStyles.getPropertyValue('--accent-color');
      
      const textColor = bodyStyles.color || '#374151';
      const bgColor = bodyStyles.backgroundColor || '#ffffff';
      
      // Detectar se o tema é escuro
      const isDark = isColorDark(bgColor);
      
      return {
        textColor: isDark ? '#f3f4f6' : textColor,
        modalBackground: isDark ? '#1f2937' : bgColor,
        itemBackground: isDark ? '#374151' : '#ffffff',
        primaryColor: primaryColor || (isDark ? '#60a5fa' : '#3b82f6')
      };
    } catch (e) {
      return {};
    }
  }
  
  function isColorDark(color) {
    if (!color || color === 'transparent') return false;
    
    // Convert to RGB
    const rgb = color.match(/\\d+/g);
    if (!rgb || rgb.length < 3) return false;
    
    const brightness = (parseInt(rgb[0]) * 299 + parseInt(rgb[1]) * 587 + parseInt(rgb[2]) * 114) / 1000;
    return brightness < 128;
  }
  
  const adaptiveColors = getAdaptiveColors();
  
  const styles = \`
    /* Reset para garantir que funcione em qualquer lugar */
    .n1p-widget-${checklist.id} * {
      box-sizing: border-box !important;
    }
    
    .n1p-float-button-${checklist.id} {
      position: fixed !important;
      ${positionStyles}
      ${buttonSize}
      padding: 0 20px !important;
      background: linear-gradient(135deg, ${customization.buttonGradientStart} 0%, ${customization.buttonGradientEnd} 100%) !important;
      border-radius: ${borderRadius} !important;
      box-shadow: ${shadowStyle} !important;
      cursor: pointer !important;
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      z-index: 999999 !important;
      transition: all 0.3s ease !important;
      border: none !important;
      font-weight: 600 !important;
      color: white !important;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
      text-decoration: none !important;
      outline: none !important;
      user-select: none !important;
      -webkit-user-select: none !important;
      -moz-user-select: none !important;
      -ms-user-select: none !important;
      gap: 2px !important;
    }
    
    .n1p-float-button-${checklist.id}:hover {
      transform: scale(1.05) !important;
      box-shadow: 0 12px 35px rgba(0, 0, 0, 0.3) !important;
    }
    
    .n1p-float-button-${checklist.id}.active {
      background: linear-gradient(135deg, ${customization.activeButtonGradientStart} 0%, ${customization.activeButtonGradientEnd} 100%) !important;
    }
    
    .n1p-button-text-${checklist.id} {
      font-size: inherit !important;
      line-height: 1 !important;
      margin: 0 !important;
    }
    
    .n1p-button-progress-${checklist.id} {
      font-size: 10px !important;
      opacity: 0.8 !important;
      font-weight: 500 !important;
      line-height: 1 !important;
      margin: 0 !important;
      white-space: nowrap !important;
    }
    
    .n1p-modal-overlay-${checklist.id} {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      background: rgba(0, 0, 0, 0.6) !important;
      backdrop-filter: blur(4px) !important;
      -webkit-backdrop-filter: blur(4px) !important;
      z-index: 1000000 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      padding: 20px !important;
      opacity: 0 !important;
      visibility: hidden !important;
      transition: all 0.3s ease !important;
    }
    
    .n1p-modal-overlay-${checklist.id}.show {
      opacity: 1 !important;
      visibility: visible !important;
    }
    
    .n1p-checklist-modal-${checklist.id} {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
      max-width: 500px !important;
      width: 100% !important;
      max-height: 80vh !important;
      background: \${adaptiveColors.modalBackground || customization.modalBackgroundColor} !important;
      border-radius: \${customization.modalBorderRadius} !important;
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25) !important;
      overflow: hidden !important;
      transform: scale(0.9) translateY(20px) !important;
      transition: all 0.3s ease !important;
      position: relative !important;
      margin: 0 !important;
    }
    
    .n1p-modal-overlay-${checklist.id}.show .n1p-checklist-modal-${checklist.id} {
      transform: scale(1) translateY(0) !important;
    }
    
    .n1p-close-button-${checklist.id} {
      position: absolute !important;
      top: 16px !important;
      right: 16px !important;
      background: rgba(0, 0, 0, 0.1) !important;
      border: none !important;
      border-radius: 50% !important;
      width: 32px !important;
      height: 32px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      cursor: pointer !important;
      color: \${adaptiveColors.textColor || customization.textColor} !important;
      font-size: 18px !important;
      transition: all 0.2s ease !important;
      z-index: 10 !important;
      font-family: inherit !important;
    }
    
    .n1p-close-button-${checklist.id}:hover {
      background: rgba(0, 0, 0, 0.2) !important;
      transform: scale(1.1) !important;
    }
    
    .n1p-checklist-header-${checklist.id} {
      padding: 24px 24px 16px 24px !important;
      border-bottom: 1px solid rgba(229, 231, 235, 0.3) !important;
    }
    
    .n1p-checklist-title-${checklist.id} {
      font-size: 20px !important;
      font-weight: 700 !important;
      margin: 0 0 8px 0 !important;
      padding-right: 40px !important;
      color: \${adaptiveColors.textColor || customization.textColor} !important;
      line-height: 1.4 !important;
    }
    
    .n1p-checklist-description-${checklist.id} {
      font-size: 14px !important;
      color: \${adaptiveColors.textColor || customization.textColor} !important;
      opacity: 0.7 !important;
      margin: 0 !important;
      padding-right: 40px !important;
      line-height: 1.4 !important;
    }
    
    .n1p-progress-section-${checklist.id} {
      padding: 0 24px 16px 24px !important;
      border-bottom: 1px solid rgba(229, 231, 235, 0.3) !important;
    }
    
    .n1p-progress-text-${checklist.id} {
      display: flex !important;
      justify-content: space-between !important;
      align-items: center !important;
      margin-bottom: 8px !important;
      font-size: 14px !important;
      font-weight: 500 !important;
      color: \${adaptiveColors.textColor || customization.textColor} !important;
    }
    
    .n1p-progress-bar-container-${checklist.id} {
      width: 100% !important;
      height: 6px !important;
      background: rgba(229, 231, 235, 0.3) !important;
      border-radius: 3px !important;
      overflow: hidden !important;
    }
    
    .n1p-progress-bar-${checklist.id} {
      height: 100% !important;
      background: \${customization.progressBarColor} !important;
      transition: width 0.3s ease !important;
      border-radius: 3px !important;
    }
    
    .n1p-checklist-items-${checklist.id} {
      padding: 24px !important;
      max-height: 400px !important;
      overflow-y: auto !important;
    }
    
    .n1p-checklist-items-${checklist.id}::-webkit-scrollbar {
      width: 6px !important;
    }
    
    .n1p-checklist-items-${checklist.id}::-webkit-scrollbar-track {
      background: rgba(241, 245, 249, 0.5) !important;
    }
    
    .n1p-checklist-items-${checklist.id}::-webkit-scrollbar-thumb {
      background: rgba(203, 213, 225, 0.8) !important;
      border-radius: 3px !important;
    }
    
    .n1p-checklist-item-${checklist.id} {
      display: flex !important;
      align-items: flex-start !important;
      gap: 12px !important;
      padding: 16px !important;
      margin-bottom: 12px !important;
      border: 1px solid \${customization.itemBorderColor} !important;
      border-radius: \${customization.itemBorderRadius} !important;
      cursor: pointer !important;
      transition: all 0.2s ease !important;
      background: \${adaptiveColors.itemBackground || customization.itemBackgroundColor} !important;
    }
    
    .n1p-checklist-item-${checklist.id}:hover {
      background: \${customization.itemHoverColor} !important;
      transform: translateY(-1px) !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    }
    
    .n1p-checklist-item-${checklist.id}.completed {
      background: \${customization.completedItemColor} !important;
      border-color: \${customization.completedBorderColor} !important;
    }
    
    .n1p-checklist-checkbox-${checklist.id} {
      width: 22px !important;
      height: 22px !important;
      border-radius: 50% !important;
      border: 2px solid #d1d5db !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex-shrink: 0 !important;
      margin-top: 2px !important;
      font-size: 14px !important;
      transition: all 0.2s ease !important;
    }
    
    .n1p-checklist-item-${checklist.id}.completed .n1p-checklist-checkbox-${checklist.id} {
      background: \${adaptiveColors.primaryColor || customization.checkboxColor} !important;
      border-color: \${adaptiveColors.primaryColor || customization.checkboxColor} !important;
      color: white !important;
    }
    
    .n1p-checklist-content-${checklist.id} {
      flex: 1 !important;
      display: flex !important;
      flex-direction: column !important;
      gap: 4px !important;
    }
    
    .n1p-checklist-item-title-${checklist.id} {
      font-weight: 600 !important;
      margin: 0 !important;
      font-size: 16px !important;
      line-height: 1.4 !important;
      color: \${adaptiveColors.textColor || customization.textColor} !important;
      display: block !important;
      width: 100% !important;
    }
    
    .n1p-checklist-item-${checklist.id}.completed .n1p-checklist-item-title-${checklist.id} {
      text-decoration: line-through !important;
      opacity: 0.7 !important;
    }
    
    .n1p-checklist-item-description-${checklist.id} {
      color: \${adaptiveColors.textColor || customization.textColor} !important;
      opacity: 0.7 !important;
      margin: 0 !important;
      font-size: 14px !important;
      line-height: 1.4 !important;
      display: block !important;
      width: 100% !important;
    }
    
    .n1p-checklist-item-${checklist.id}.completed .n1p-checklist-item-description-${checklist.id} {
      text-decoration: line-through !important;
      opacity: 0.5 !important;
    }
    
    .n1p-checklist-link-${checklist.id} {
      color: \${adaptiveColors.primaryColor || customization.linkColor} !important;
      text-decoration: none !important;
      font-size: 14px !important;
      display: block !important;
      width: fit-content !important;
      padding: 6px 12px !important;
      border-radius: 6px !important;
      background: rgba(59, 130, 246, 0.1) !important;
      transition: all 0.2s ease !important;
      margin-top: 4px !important;
      font-weight: 500 !important;
    }
    
    .n1p-checklist-link-${checklist.id}:hover {
      background: rgba(59, 130, 246, 0.2) !important;
      transform: translateY(-1px) !important;
    }
    
    /* Responsividade aprimorada */
    @media (max-width: 640px) {
      .n1p-float-button-${checklist.id} {
        bottom: 20px !important;
        right: 20px !important;
        min-width: 50px !important;
        height: 50px !important;
        font-size: 14px !important;
      }
      
      .n1p-button-progress-${checklist.id} {
        font-size: 9px !important;
      }
      
      .n1p-checklist-modal-${checklist.id} {
        margin: 10px !important;
        max-height: 85vh !important;
      }
      
      .n1p-checklist-header-${checklist.id},
      .n1p-checklist-items-${checklist.id},
      .n1p-progress-section-${checklist.id} {
        padding: 16px !important;
      }
      
      .n1p-checklist-item-${checklist.id} {
        padding: 12px !important;
      }
      
      .n1p-checklist-items-${checklist.id} {
        max-height: 350px !important;
      }
    }
    
    @media (max-width: 480px) {
      .n1p-modal-overlay-${checklist.id} {
        padding: 10px !important;
      }
      
      .n1p-checklist-modal-${checklist.id} {
        margin: 0 !important;
        border-radius: 12px !important;
      }
    }
  \`;
  
  function createWidget() {
    // Criar container se não existir
    let container = document.getElementById('n1p-checklist-${checklist.id}');
    if (!container) {
      container = document.createElement('div');
      container.id = 'n1p-checklist-${checklist.id}';
      container.className = 'n1p-widget-${checklist.id}';
      document.body.appendChild(container);
    }
    
    // Adicionar estilos
    const styleSheet = document.createElement('style');
    styleSheet.id = 'n1p-styles-${checklist.id}';
    styleSheet.textContent = styles;
    
    // Remover estilo anterior se existir
    const existingStyle = document.getElementById('n1p-styles-${checklist.id}');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    document.head.appendChild(styleSheet);
    
    let completedItems = new Set();
    let isModalOpen = false;
    
    function getProgress() {
      const totalItems = checklistData.items.length;
      const completedCount = completedItems.size;
      const percentage = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;
      return { completed: completedCount, total: totalItems, percentage };
    }
    
    function updateButtonText() {
      const button = container.querySelector('.n1p-float-button-${checklist.id}');
      if (!button || isModalOpen) return;
      
      const progress = getProgress();
      const textElement = button.querySelector('.n1p-button-text-${checklist.id}');
      const progressElement = button.querySelector('.n1p-button-progress-${checklist.id}');
      
      if (textElement) {
        textElement.textContent = customization.buttonText;
      }
      
      if (progressElement && customization.showProgressInButton) {
        progressElement.textContent = \`\${progress.completed}/\${progress.total}\`;
        progressElement.style.display = 'block';
      } else if (progressElement) {
        progressElement.style.display = 'none';
      }
    }
    
    function toggleModal() {
      isModalOpen = !isModalOpen;
      const modal = container.querySelector('.n1p-modal-overlay-${checklist.id}');
      const button = container.querySelector('.n1p-float-button-${checklist.id}');
      
      if (isModalOpen) {
        modal.classList.add('show');
        button.classList.add('active');
        const textElement = button.querySelector('.n1p-button-text-${checklist.id}');
        const progressElement = button.querySelector('.n1p-button-progress-${checklist.id}');
        if (textElement) textElement.textContent = '✕';
        if (progressElement) progressElement.style.display = 'none';
        document.body.style.overflow = 'hidden';
      } else {
        modal.classList.remove('show');
        button.classList.remove('active');
        updateButtonText();
        document.body.style.overflow = '';
      }
    }
    
    function toggleItem(itemId) {
      if (completedItems.has(itemId)) {
        completedItems.delete(itemId);
      } else {
        completedItems.add(itemId);
      }
      
      const itemElement = container.querySelector(\`[data-item-id="\${itemId}"]\`);
      if (itemElement) {
        itemElement.classList.toggle('completed', completedItems.has(itemId));
        const checkbox = itemElement.querySelector('.n1p-checklist-checkbox-${checklist.id}');
        checkbox.innerHTML = completedItems.has(itemId) ? '✓' : '';
      }
      
      // Atualizar barra de progresso
      updateProgressBar();
      
      // Atualizar texto do botão
      updateButtonText();
      
      // Salvar progresso no localStorage
      try {
        localStorage.setItem(\`n1p-checklist-\${checklistData.id}-progress\`, JSON.stringify([...completedItems]));
      } catch (e) {
        console.warn('Não foi possível salvar o progresso:', e);
      }
    }
    
    function updateProgressBar() {
      const progress = getProgress();
      const progressBar = container.querySelector('.n1p-progress-bar-${checklist.id}');
      const progressText = container.querySelector('.n1p-progress-text-${checklist.id}');
      
      if (progressBar) {
        progressBar.style.width = \`\${progress.percentage}%\`;
      }
      
      if (progressText) {
        progressText.innerHTML = \`
          <span>Progresso: \${progress.completed} de \${progress.total} marcos</span>
          <span>\${progress.percentage}%</span>
        \`;
      }
    }
    
    // Carregar progresso salvo
    try {
      const savedProgress = localStorage.getItem(\`n1p-checklist-\${checklistData.id}-progress\`);
      if (savedProgress) {
        completedItems = new Set(JSON.parse(savedProgress));
      }
    } catch (e) {
      console.warn('Erro ao carregar progresso salvo:', e);
    }
    
    function render() {
      const headerContent = (customization.showTitle || customization.showDescription) ? \`
        <div class="n1p-checklist-header-\${checklistData.id}">
          \${customization.showTitle ? \`<h3 class="n1p-checklist-title-\${checklistData.id}">\${checklistData.title}</h3>\` : ''}
          \${customization.showDescription ? \`<p class="n1p-checklist-description-\${checklistData.id}">\${checklistData.description}</p>\` : ''}
        </div>
      \` : '';
      
      const progressSection = customization.showProgressBar ? \`
        <div class="n1p-progress-section-\${checklistData.id}">
          <div class="n1p-progress-text-\${checklistData.id}">
            <span>Progresso: 0 de \${checklistData.items.length} marcos</span>
            <span>0%</span>
          </div>
          <div class="n1p-progress-bar-container-\${checklistData.id}">
            <div class="n1p-progress-bar-\${checklistData.id}" style="width: 0%"></div>
          </div>
        </div>
      \` : '';
      
      const progress = getProgress();
      
      container.innerHTML = \`
        <button class="n1p-float-button-\${checklistData.id}" onclick="window.n1pToggleModal_\${checklistData.id}()">
          <div class="n1p-button-text-\${checklistData.id}">\${customization.buttonText}</div>
          \${customization.showProgressInButton ? \`<div class="n1p-button-progress-\${checklistData.id}">\${progress.completed}/\${progress.total}</div>\` : ''}
        </button>
        
        <div class="n1p-modal-overlay-\${checklistData.id}" onclick="window.n1pCloseModal_\${checklistData.id}(event)">
          <div class="n1p-checklist-modal-\${checklistData.id}" onclick="event.stopPropagation()">
            <button class="n1p-close-button-\${checklistData.id}" onclick="window.n1pToggleModal_\${checklistData.id}()">
              ✕
            </button>
            \${headerContent}
            \${progressSection}
            <div class="n1p-checklist-items-\${checklistData.id}">
              \${checklistData.items.map(item => \`
                <div class="n1p-checklist-item-\${checklistData.id} \${completedItems.has(item.id) ? 'completed' : ''}" data-item-id="\${item.id}" onclick="window.n1pToggleItem_\${checklistData.id}(\${item.id})">
                  <div class="n1p-checklist-checkbox-\${checklistData.id}">\${completedItems.has(item.id) ? '✓' : ''}</div>
                  <div class="n1p-checklist-content-\${checklistData.id}">
                    <div class="n1p-checklist-item-title-\${checklistData.id}">\${item.title}</div>
                    <div class="n1p-checklist-item-description-\${checklistData.id}">\${item.description}</div>
                    \${item.link ? \`<a href="\${item.link}" target="_blank" rel="noopener noreferrer" class="n1p-checklist-link-\${checklistData.id}" onclick="event.stopPropagation()">Acessar ↗</a>\` : ''}
                  </div>
                </div>
              \`).join('')}
            </div>
          </div>
        </div>
      \`;
      
      // Atualizar progresso inicial
      setTimeout(() => {
        updateProgressBar();
        updateButtonText();
      }, 10);
    }
    
    // Funções globais
    window[\`n1pToggleModal_\${checklistData.id}\`] = toggleModal;
    window[\`n1pToggleItem_\${checklistData.id}\`] = toggleItem;
    window[\`n1pCloseModal_\${checklistData.id}\`] = function(event) {
      if (event.target.classList.contains(\`n1p-modal-overlay-\${checklistData.id}\`)) {
        toggleModal();
      }
    };
    
    render();
  }
  
  // Executar quando o DOM estiver pronto
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', createWidget);
  } else {
    createWidget();
  }
  
  // Executar novamente após um pequeno delay para garantir compatibilidade com page builders
  setTimeout(createWidget, 100);
})();
</script>`;
  };

  const generateReactCode = () => {
    return `import React, { useState, useEffect } from 'react';

const N1PChecklistFloating = ({ 
  checklist = ${JSON.stringify(checklist, null, 2)},
  customization = {
    buttonText: "${customization.buttonText}",
    buttonPosition: "${customization.buttonPosition}",
    buttonGradientStart: "${customization.buttonGradientStart}",
    buttonGradientEnd: "${customization.buttonGradientEnd}",
    activeButtonGradientStart: "${customization.activeButtonGradientStart}",
    activeButtonGradientEnd: "${customization.activeButtonGradientEnd}",
    buttonSize: "${customization.buttonSize}",
    buttonBorderRadius: "${customization.buttonBorderRadius}",
    buttonShadow: "${customization.buttonShadow}",
    adaptiveStyle: ${customization.adaptiveStyle},
    showProgressInButton: ${customization.showProgressInButton},
    showProgressBar: ${customization.showProgressBar},
    progressBarColor: "${customization.progressBarColor}",
    showTitle: ${customization.showTitle},
    showDescription: ${customization.showDescription},
    itemBackgroundColor: "${customization.itemBackgroundColor}",
    itemBorderColor: "${customization.itemBorderColor}",
    itemHoverColor: "${customization.itemHoverColor}",
    completedItemColor: "${customization.completedItemColor}",
    completedBorderColor: "${customization.completedBorderColor}",
    checkboxColor: "${customization.checkboxColor}",
    textColor: "${customization.textColor}",
    linkColor: "${customization.linkColor}",
    modalBackgroundColor: "${customization.modalBackgroundColor}",
    modalBorderRadius: "${customization.modalBorderRadius}",
    itemBorderRadius: "${customization.itemBorderRadius}"
  }
}) => {
  const [completedItems, setCompletedItems] = useState(new Set());
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Carregar progresso salvo
  useEffect(() => {
    try {
      const savedProgress = localStorage.getItem(\`n1p-checklist-\${checklist.id}-progress\`);
      if (savedProgress) {
        setCompletedItems(new Set(JSON.parse(savedProgress)));
      }
    } catch (e) {
      console.error('Erro ao carregar progresso salvo:', e);
    }
  }, [checklist.id]);

  const toggleItem = (itemId) => {
    const newCompleted = new Set(completedItems);
    if (newCompleted.has(itemId)) {
      newCompleted.delete(itemId);
    } else {
      newCompleted.add(itemId);
    }
    setCompletedItems(newCompleted);
    
    // Salvar progresso
    localStorage.setItem(\`n1p-checklist-\${checklist.id}-progress\`, 
      JSON.stringify([...newCompleted]));
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    document.body.style.overflow = !isModalOpen ? 'hidden' : '';
  };

  const getProgress = () => {
    const totalItems = checklist.items.length;
    const completedCount = completedItems.size;
    const percentage = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;
    return { completed: completedCount, total: totalItems, percentage };
  };

  const progress = getProgress();

  // Definir posição do botão
  const getButtonPosition = () => {
    switch(customization.buttonPosition) {
      case 'bottom-right': return 'bottom-8 right-8';
      case 'bottom-left': return 'bottom-8 left-8';
      case 'top-right': return 'top-8 right-8';
      case 'top-left': return 'top-8 left-8';
      case 'middle-right': return 'top-1/2 -translate-y-1/2 right-8';
      case 'middle-left': return 'top-1/2 -translate-y-1/2 left-8';
      default: return 'bottom-8 right-8';
    }
  };

  // Obter tamanho do botão
  const getButtonSize = () => {
    switch(customization.buttonSize) {
      case 'small': return 'min-w-[45px] h-[45px] text-sm';
      case 'medium': return 'min-w-[60px] h-[60px] text-base';
      case 'large': return 'min-w-[75px] h-[75px] text-lg';
      default: return 'min-w-[60px] h-[60px] text-base';
    }
  };

  // Obter border radius do botão
  const getBorderRadius = () => {
    switch(customization.buttonBorderRadius) {
      case 'none': return 'rounded-none';
      case 'small': return 'rounded-md';
      case 'medium': return 'rounded-xl';
      case 'large': return 'rounded-3xl';
      case 'full': return 'rounded-full';
      default: return 'rounded-full';
    }
  };

  // Obter shadow do botão
  const getShadow = () => {
    switch(customization.buttonShadow) {
      case 'none': return '';
      case 'small': return 'shadow-sm';
      case 'medium': return 'shadow-lg';
      case 'large': return 'shadow-xl';
      default: return 'shadow-lg';
    }
  };

  return (
    <>
      {/* Botão Flutuante */}
      <button
        onClick={toggleModal}
        className={\`fixed \${getButtonPosition()} \${getButtonSize()} px-5 \${getBorderRadius()} \${getShadow()} hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex flex-col items-center justify-center font-semibold z-50 text-white gap-0.5\`}
        style={{
          background: isModalOpen 
            ? \`linear-gradient(135deg, \${customization.activeButtonGradientStart}, \${customization.activeButtonGradientEnd})\`
            : \`linear-gradient(135deg, \${customization.buttonGradientStart}, \${customization.buttonGradientEnd})\`,
          zIndex: 999999
        }}
      >
        <div className="leading-none">
          {isModalOpen ? '✕' : customization.buttonText}
        </div>
        {!isModalOpen && customization.showProgressInButton && (
          <div className="text-xs opacity-80 font-medium leading-none">
            {progress.completed}/{progress.total}
          </div>
        )}
      </button>

      {/* Modal Overlay */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50 flex items-center justify-center p-5"
          style={{ zIndex: 1000000 }}
          onClick={toggleModal}
        >
          <div
            className="max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300 relative"
            style={{
              backgroundColor: customization.modalBackgroundColor,
              borderRadius: customization.modalBorderRadius
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={toggleModal}
              className="absolute top-4 right-4 w-8 h-8 bg-black bg-opacity-10 rounded-full flex items-center justify-center hover:bg-opacity-20 transition-all z-10"
              style={{ color: customization.textColor }}
            >
              ✕
            </button>
            
            {/* Header (opcional) */}
            {(customization.showTitle || customization.showDescription) && (
              <div className="p-6 pb-4 border-b border-gray-200">
                {customization.showTitle && (
                  <h3 className="text-xl font-bold mb-2 pr-10" style={{ color: customization.textColor }}>
                    {checklist.title}
                  </h3>
                )}
                {customization.showDescription && (
                  <p className="text-sm pr-10 leading-5" style={{ color: customization.textColor, opacity: 0.7 }}>
                    {checklist.description}
                  </p>
                )}
              </div>
            )}
            
            {/* Progress Section */}
            {customization.showProgressBar && (
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center mb-2 text-sm font-medium" style={{ color: customization.textColor }}>
                  <span>Progresso: {progress.completed} de {progress.total} marcos</span>
                  <span>{progress.percentage}%</span>
                </div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full transition-all duration-300 rounded-full"
                    style={{ 
                      width: \`\${progress.percentage}%\`,
                      backgroundColor: customization.progressBarColor
                    }}
                  />
                </div>
              </div>
            )}
            
            {/* Items */}
            <div className="p-6 max-h-96 overflow-y-auto space-y-3">
              {checklist.items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 p-4 rounded-xl cursor-pointer transition-all hover:transform hover:-translate-y-0.5"
                  style={{
                    backgroundColor: completedItems.has(item.id) 
                      ? customization.completedItemColor 
                      : customization.itemBackgroundColor,
                    borderColor: completedItems.has(item.id) 
                      ? customization.completedBorderColor 
                      : customization.itemBorderColor,
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderRadius: customization.itemBorderRadius
                  }}
                  onClick={() => toggleItem(item.id)}
                >
                  <div 
                    className="w-5.5 h-5.5 rounded-full border-2 flex items-center justify-center text-sm mt-0.5 transition-all text-white"
                    style={{ 
                      width: '22px', 
                      height: '22px',
                      backgroundColor: completedItems.has(item.id) ? customization.checkboxColor : 'transparent',
                      borderColor: completedItems.has(item.id) ? customization.checkboxColor : '#d1d5db'
                    }}
                  >
                    {completedItems.has(item.id) && '✓'}
                  </div>
                  <div className="flex-1 flex flex-col gap-1">
                    <div 
                      className={\`font-semibold leading-5 block w-full \${completedItems.has(item.id) ? 'line-through opacity-70' : ''}\`}
                      style={{ color: customization.textColor }}
                    >
                      {item.title}
                    </div>
                    <div 
                      className={\`text-sm leading-5 block w-full \${completedItems.has(item.id) ? 'line-through opacity-50' : 'opacity-70'}\`}
                      style={{ color: customization.textColor }}
                    >
                      {item.description}
                    </div>
                    {item.link && (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-fit px-3 py-1.5 rounded-md transition-all mt-1 font-medium"
                        style={{ 
                          color: customization.linkColor,
                          backgroundColor: \`\${customization.linkColor}20\`
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Acessar ↗
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default N1PChecklistFloating;`;
  };

  const getCode = () => {
    return codeType === 'widget' ? generateWidgetCode() : generateReactCode();
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getCode());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  const downloadCode = () => {
    const code = getCode();
    const filename = codeType === 'widget' ? `n1p-checklist-floating-${checklist.id}.html` : `N1PChecklistFloating.jsx`;
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`rounded-xl p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiCode} className="text-xl" />
          <h3 className="text-lg font-bold">Widget Flutuante Universal</h3>
        </div>
        
        <div className="flex items-center space-x-2">
          <select
            value={codeType}
            onChange={(e) => setCodeType(e.target.value)}
            className={`px-3 py-1 rounded border text-sm ${
              darkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300'
            }`}
          >
            <option value="widget">Widget HTML/JS</option>
            <option value="react">Componente React</option>
          </select>
          
          <button
            onClick={downloadCode}
            className="p-2 text-blue-600 hover:text-blue-700 rounded"
            title="Download"
          >
            <SafeIcon icon={FiDownload} />
          </button>
          
          <button
            onClick={copyToClipboard}
            className="p-2 text-blue-600 hover:text-blue-700 rounded"
            title="Copiar código"
          >
            <SafeIcon icon={copied ? FiCheck : FiCopy} />
          </button>
        </div>
      </div>

      {/* Customization Section */}
      <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
        <div className="flex items-center mb-4">
          <SafeIcon icon={FiSliders} className="mr-2" />
          <h4 className="font-bold">Personalizar Widget</h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Botão */}
          <div>
            <h5 className="font-semibold mb-3 text-sm">🔘 Botão</h5>
            <div className="space-y-3">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Texto do Botão
                </label>
                <input
                  type="text"
                  value={customization.buttonText}
                  onChange={(e) => handleCustomizationChange('buttonText', e.target.value)}
                  className={`w-full px-3 py-2 rounded border text-sm ${
                    darkMode 
                      ? 'bg-gray-800 border-gray-600 text-white' 
                      : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={customization.showProgressInButton}
                    onChange={(e) => handleCustomizationChange('showProgressInButton', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Mostrar Progresso no Botão</span>
                </label>
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Posição
                </label>
                <select
                  value={customization.buttonPosition}
                  onChange={(e) => handleCustomizationChange('buttonPosition', e.target.value)}
                  className={`w-full px-3 py-2 rounded border text-sm ${
                    darkMode 
                      ? 'bg-gray-800 border-gray-600 text-white' 
                      : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="bottom-right">Inferior Direito</option>
                  <option value="bottom-left">Inferior Esquerdo</option>
                  <option value="top-right">Superior Direito</option>
                  <option value="top-left">Superior Esquerdo</option>
                  <option value="middle-right">Meio Direito</option>
                  <option value="middle-left">Meio Esquerdo</option>
                </select>
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Tamanho
                </label>
                <select
                  value={customization.buttonSize}
                  onChange={(e) => handleCustomizationChange('buttonSize', e.target.value)}
                  className={`w-full px-3 py-2 rounded border text-sm ${
                    darkMode 
                      ? 'bg-gray-800 border-gray-600 text-white' 
                      : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="small">Pequeno</option>
                  <option value="medium">Médio</option>
                  <option value="large">Grande</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Curvatura das Bordas
                </label>
                <select
                  value={customization.buttonBorderRadius}
                  onChange={(e) => handleCustomizationChange('buttonBorderRadius', e.target.value)}
                  className={`w-full px-3 py-2 rounded border text-sm ${
                    darkMode 
                      ? 'bg-gray-800 border-gray-600 text-white' 
                      : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="none">Sem Curvatura</option>
                  <option value="small">Pequena</option>
                  <option value="medium">Média</option>
                  <option value="large">Grande</option>
                  <option value="full">Totalmente Arredondado</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Sombra
                </label>
                <select
                  value={customization.buttonShadow}
                  onChange={(e) => handleCustomizationChange('buttonShadow', e.target.value)}
                  className={`w-full px-3 py-2 rounded border text-sm ${
                    darkMode 
                      ? 'bg-gray-800 border-gray-600 text-white' 
                      : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="none">Sem Sombra</option>
                  <option value="small">Pequena</option>
                  <option value="medium">Média</option>
                  <option value="large">Grande</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Cor Inicial
                  </label>
                  <input
                    type="color"
                    value={customization.buttonGradientStart}
                    onChange={(e) => handleCustomizationChange('buttonGradientStart', e.target.value)}
                    className="w-full h-8 rounded border cursor-pointer"
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Cor Final
                  </label>
                  <input
                    type="color"
                    value={customization.buttonGradientEnd}
                    onChange={(e) => handleCustomizationChange('buttonGradientEnd', e.target.value)}
                    className="w-full h-8 rounded border cursor-pointer"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Modal e Progresso */}
          <div>
            <h5 className="font-semibold mb-3 text-sm">📋 Modal e Progresso</h5>
            <div className="space-y-3">
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={customization.showTitle}
                    onChange={(e) => handleCustomizationChange('showTitle', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Mostrar Título</span>
                </label>
              </div>
              
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={customization.showDescription}
                    onChange={(e) => handleCustomizationChange('showDescription', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Mostrar Descrição</span>
                </label>
              </div>

              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={customization.showProgressBar}
                    onChange={(e) => handleCustomizationChange('showProgressBar', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Mostrar Barra de Progresso</span>
                </label>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Cor da Barra de Progresso
                </label>
                <input
                  type="color"
                  value={customization.progressBarColor}
                  onChange={(e) => handleCustomizationChange('progressBarColor', e.target.value)}
                  className="w-full h-8 rounded border cursor-pointer"
                />
              </div>

              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={customization.adaptiveStyle}
                    onChange={(e) => handleCustomizationChange('adaptiveStyle', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Estilo Adaptativo</span>
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Adapta as cores ao tema do site automaticamente
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Fundo do Modal
                </label>
                <input
                  type="color"
                  value={customization.modalBackgroundColor}
                  onChange={(e) => handleCustomizationChange('modalBackgroundColor', e.target.value)}
                  className="w-full h-8 rounded border cursor-pointer"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Cor do Texto
                </label>
                <input
                  type="color"
                  value={customization.textColor}
                  onChange={(e) => handleCustomizationChange('textColor', e.target.value)}
                  className="w-full h-8 rounded border cursor-pointer"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Cor dos Links
                </label>
                <input
                  type="color"
                  value={customization.linkColor}
                  onChange={(e) => handleCustomizationChange('linkColor', e.target.value)}
                  className="w-full h-8 rounded border cursor-pointer"
                />
              </div>
            </div>
          </div>

          {/* Itens */}
          <div>
            <h5 className="font-semibold mb-3 text-sm">✅ Itens</h5>
            <div className="space-y-3">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Fundo do Item
                </label>
                <input
                  type="color"
                  value={customization.itemBackgroundColor}
                  onChange={(e) => handleCustomizationChange('itemBackgroundColor', e.target.value)}
                  className="w-full h-8 rounded border cursor-pointer"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Borda do Item
                </label>
                <input
                  type="color"
                  value={customization.itemBorderColor}
                  onChange={(e) => handleCustomizationChange('itemBorderColor', e.target.value)}
                  className="w-full h-8 rounded border cursor-pointer"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Hover do Item
                </label>
                <input
                  type="color"
                  value={customization.itemHoverColor}
                  onChange={(e) => handleCustomizationChange('itemHoverColor', e.target.value)}
                  className="w-full h-8 rounded border cursor-pointer"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Item Concluído
                </label>
                <input
                  type="color"
                  value={customization.completedItemColor}
                  onChange={(e) => handleCustomizationChange('completedItemColor', e.target.value)}
                  className="w-full h-8 rounded border cursor-pointer"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Checkbox
                </label>
                <input
                  type="color"
                  value={customization.checkboxColor}
                  onChange={(e) => handleCustomizationChange('checkboxColor', e.target.value)}
                  className="w-full h-8 rounded border cursor-pointer"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="mt-6 p-4 border rounded-lg relative overflow-hidden">
          <div className="text-center font-medium mb-4">Preview do Widget Universal</div>
          
          <div className="flex justify-center mb-4">
            <button
              className={`px-4 flex flex-col items-center justify-center font-semibold transition-all hover:scale-105 gap-0.5`}
              style={{
                background: `linear-gradient(135deg, ${customization.buttonGradientStart}, ${customization.buttonGradientEnd})`,
                color: 'white',
                borderRadius: getBorderRadiusStyle(),
                boxShadow: getShadowStyle(),
                ...({
                  'small': { minWidth: '45px', height: '45px', fontSize: '14px' },
                  'medium': { minWidth: '60px', height: '60px', fontSize: '16px' },
                  'large': { minWidth: '75px', height: '75px', fontSize: '18px' }
                })[customization.buttonSize]
              }}
            >
              <div>{customization.buttonText}</div>
              {customization.showProgressInButton && (
                <div style={{ fontSize: '10px', opacity: 0.8 }}>0/{checklist.items.length}</div>
              )}
            </button>
          </div>

          <div className="text-center text-sm text-gray-600 mb-4">
            <div className="inline-flex items-center space-x-2">
              <span>Progresso no botão:</span>
              <span className="font-medium">
                {customization.showProgressInButton ? 'Ativado' : 'Desativado'}
              </span>
            </div>
          </div>

          <div className="max-w-sm mx-auto border rounded-lg overflow-hidden" style={{ 
            backgroundColor: customization.modalBackgroundColor,
            borderRadius: customization.modalBorderRadius 
          }}>
            {(customization.showTitle || customization.showDescription) && (
              <div className="p-4 border-b border-gray-200">
                {customization.showTitle && (
                  <h4 className="font-bold text-sm mb-1" style={{ color: customization.textColor }}>
                    {checklist.title}
                  </h4>
                )}
                {customization.showDescription && (
                  <p className="text-xs opacity-70" style={{ color: customization.textColor }}>
                    {checklist.description}
                  </p>
                )}
              </div>
            )}
            
            {customization.showProgressBar && (
              <div className="p-4 border-b border-gray-200">
                <div className="flex justify-between items-center mb-2 text-xs font-medium" style={{ color: customization.textColor }}>
                  <span>Progresso: 0 de {checklist.items.length} marcos</span>
                  <span>0%</span>
                </div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full"
                    style={{ 
                      width: '0%',
                      backgroundColor: customization.progressBarColor
                    }}
                  />
                </div>
              </div>
            )}
            
            <div className="p-4 space-y-2">
              {checklist.items.slice(0, 2).map((item, index) => (
                <div key={item.id} className="flex items-start gap-2 p-2 rounded text-xs" style={{
                  backgroundColor: index === 0 ? customization.completedItemColor : customization.itemBackgroundColor,
                  borderColor: index === 0 ? customization.completedBorderColor : customization.itemBorderColor,
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderRadius: customization.itemBorderRadius
                }}>
                  <div 
                    className="w-4 h-4 rounded-full border flex items-center justify-center text-white text-xs"
                    style={{ 
                      backgroundColor: index === 0 ? customization.checkboxColor : 'transparent',
                      borderColor: index === 0 ? customization.checkboxColor : '#d1d5db'
                    }}
                  >
                    {index === 0 && '✓'}
                  </div>
                  <div className="flex-1 flex flex-col gap-0.5">
                    <div className={`font-medium block w-full ${index === 0 ? 'line-through opacity-70' : ''}`} style={{ color: customization.textColor }}>
                      {item.title}
                    </div>
                    <div className={`text-xs block w-full ${index === 0 ? 'line-through opacity-50' : 'opacity-70'}`} style={{ color: customization.textColor }}>
                      {item.description}
                    </div>
                    {item.link && (
                      <div className="block w-fit px-2 py-0.5 rounded text-xs mt-0.5" style={{ 
                        color: customization.linkColor,
                        backgroundColor: `${customization.linkColor}20`
                      }}>
                        Acessar ↗
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {checklist.items.length > 2 && (
                <div className="text-center text-xs opacity-50" style={{ color: customization.textColor }}>
                  +{checklist.items.length - 2} mais...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <div className={`p-3 rounded-lg text-sm ${
          darkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-200'
        }`}>
          <p className="font-medium mb-2">
            🚀 Widget Universal com Contador de Progresso:
          </p>
          <ul className="space-y-1 text-xs">
            <li>• <strong>Contador no botão</strong> - mostra progresso sutil (ex: 2/5)</li>
            <li>• <strong>Barra de progresso</strong> - visual completo no modal</li>
            <li>• <strong>Atualização em tempo real</strong> - progresso atualiza automaticamente</li>
            <li>• <strong>Persistência local</strong> - progresso salvo no navegador</li>
            <li>• <strong>100% Compatível</strong> - WordPress, Elementor, Shopify, HTML puro</li>
            <li>• <strong>Layout em coluna garantido</strong> - título, descrição e link sempre organizados verticalmente</li>
          </ul>
        </div>
      </div>

      <div className="relative">
        <textarea
          ref={textareaRef}
          value={getCode()}
          readOnly
          className={`w-full h-96 p-4 rounded-lg border font-mono text-xs resize-none ${
            darkMode 
              ? 'bg-gray-700 border-gray-600 text-gray-300' 
              : 'bg-gray-50 border-gray-300 text-gray-700'
          }`}
        />
        
        {copied && (
          <div className="absolute top-2 right-2 bg-green-600 text-white px-2 py-1 rounded text-xs">
            Copiado!
          </div>
        )}
      </div>

      <div className={`mt-4 p-4 rounded-lg ${
        darkMode ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'
      }`}>
        <h4 className="font-semibold mb-2 text-blue-800 dark:text-blue-300">
          💡 Funcionalidades do Contador de Progresso:
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-blue-700 dark:text-blue-300">
          <ul className="space-y-1">
            <li>✅ <strong>Botão inteligente:</strong> Mostra "2/5" discretamente</li>
            <li>✅ <strong>Barra visual:</strong> Progresso % no modal</li>
            <li>✅ <strong>Cores personalizáveis:</strong> Combine com seu tema</li>
            <li>✅ <strong>Responsivo:</strong> Adapta-se a qualquer tela</li>
          </ul>
          <ul className="space-y-1">
            <li>🎯 Progresso salvo automaticamente</li>
            <li>🎯 Atualização em tempo real</li>
            <li>🎯 Layout em coluna garantido</li>
            <li>🎯 Funciona em qualquer site</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CodeGenerator;